import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import styles from './Table.module.css';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const navigate = useNavigate();

  const fetchUsers = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (search) params.append('q', search);
      if (filterActive !== '') params.append('isActive', filterActive);
      const res = await api.get(`/admin/users?${params}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, filterActive]);

  useEffect(() => { fetchUsers(page); }, [page, fetchUsers]);

  const handleToggle = async (id, current) => {
    const label = current ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${label} this user?`)) return;
    try {
      await api.patch(`/admin/users/${id}/toggle`);
      fetchUsers(page);
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const handleVerify = async (id, current) => {
    const label = current ? 'Remove verification' : 'Verify seller';
    if (!window.confirm(`${label}?`)) return;
    try {
      await api.patch(`/admin/users/${id}/verify`);
      fetchUsers(page);
    } catch (err) {
      alert(err.response?.data?.message || 'Error');
    }
  };

  const handleDelete = async (id, label) => {
    const name = label || 'this user';
    if (
      !window.confirm(
        `Delete ${name}? Their ads and chats will also be removed. This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/admin/users/${id}`);
      fetchUsers(page);
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers(1);
  };

  const pages = Math.ceil(total / 20);

  return (
    <div>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Users ({total})</h2>
        <div className={styles.headerRight}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
            <input
              className={styles.searchInput}
              placeholder="Search name, email, phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button type="submit" className={styles.addBtn}>Search</button>
          </form>
          <select
            className={styles.filterSelect}
            value={filterActive}
            onChange={e => { setFilterActive(e.target.value); setPage(1); }}
          >
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {loading ? <p>Loading...</p> : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Package</th>
                <th>Status</th>
                <th>Verified</th>
                <th>Joined</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={8} className={styles.empty}>No users found</td></tr>
              )}
              {users.map(u => (
                <tr key={u._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/users/${u._id}`)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: '#e8f5e9', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontWeight: 700, color: '#6CA651', fontSize: 15,
                        flexShrink: 0,
                      }}>
                        {(u.name || u.email || '?')[0].toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600 }}>{u.name || <span style={{ color: '#ccc' }}>—</span>}</span>
                    </div>
                  </td>
                  <td style={{ color: '#555' }}>{u.email}</td>
                  <td style={{ color: '#555' }}>{u.phone || '—'}</td>
                  <td>
                    {u.activePackage?.name ? (
                      <span className={styles.badgeFeatured}>{u.activePackage.name}</span>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={u.isActive ? styles.badgeActive : styles.badgeInactive}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                    {u.isBlocked && (
                      <span style={{
                        marginLeft: 6, background: '#ffeaea', color: '#cc0000',
                        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      }}>Blocked</span>
                    )}
                  </td>
                  <td>
                    {u.isVerified ? (
                      <span style={{
                        background: '#e8f5e9', color: '#2e7d32',
                        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      }}>Verified</span>
                    ) : (
                      <span style={{ color: '#aaa', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ color: '#888', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {new Date(u.createdAt).toLocaleDateString('en-PK', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </td>
                  <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      className={u.isVerified ? styles.deleteBtn : styles.editBtn}
                      onClick={() => handleVerify(u._id, u.isVerified)}
                    >
                      {u.isVerified ? 'Unverify' : 'Verify'}
                    </button>
                    <button
                      className={u.isActive ? styles.deleteBtn : styles.editBtn}
                      onClick={() => handleToggle(u._id, u.isActive)}
                    >
                      {u.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(u._id, u.name || u.phone || u.email)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} style={{
              padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd',
              background: p === page ? '#6366f1' : '#fff',
              color: p === page ? '#fff' : '#333', cursor: 'pointer',
            }}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
}
